import { Text } from '@mantine/core';
import type { TFunction } from 'i18next';
import { DefaultValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Abstract } from '@/module/Author/Abstract';
import { AbstractCard } from '@/module/Author/Abstract/components/Card/AbstractCard';
import { Article } from '@/module/Author/Article';
import { ArticleCard } from '@/module/Author/Article/components/Card/ArticleCard';
import { Book } from '@/module/Author/Book';
import { BookCard } from '@/module/Author/Book/components/Card/BookCard';
import { Chapter } from '@/module/Author/Chapter';
import { ChapterCard } from '@/module/Author/Chapter/components/Card/ChapterCard';
import { Committee } from '@/module/Author/Committee';
import { CommitteeCard } from '@/module/Author/Committee/components/Card/CommitteeCard';
import { Course } from '@/module/Author/Course';
import { CourseCard } from '@/module/Author/Course/components/Card/CourseCard';
import { DoctoralCommittee } from '@/module/Author/DoctoralCommittee';
import { DoctoralCommitteeCard } from '@/module/Author/DoctoralCommittee/components/Card/DoctoralCommitteeCard';
import { EditorialTeam } from '@/module/Author/EditorialTeam';
import { EditorialTeamCard } from '@/module/Author/EditorialTeam/components/Card/EditorialTeamCard';
import { Patent } from '@/module/Author/Patent';
import { PatentCard } from '@/module/Author/Patent/components/Card/PatentCard';
import { Proceeding } from '@/module/Author/Proceeding';
import { ProceedingCard } from '@/module/Author/Proceeding/components/Card/ProceedingCard';
import { Project } from '@/module/Author/Project';
import { ProjectCard } from '@/module/Author/Project/components/Card/ProjectCard';
import { Translation } from '@/module/Author/Translation';
import { TranslationCard } from '@/module/Author/Translation/components/Card/TranslationCard';
import { BaseEvaluationCriteriaItemForm } from '@/module/Evaluation/form/BaseEvaluationCriteriaItemForm';
import { BaseEvaluationSelectControl } from '@/module/Evaluation/form/BaseEvaluationSelectControl';
import {
  BaseCriteriaItemSchema,
  abstractCriteriaItemSchema,
  articleCriteriaItemSchema,
  bookCriteriaItemSchema,
  booleanCriteriaItemSchema,
  chapterCriteriaItemSchema,
  committeeCriteriaItemSchema,
  courseCriteriaItemSchema,
  doctoralCommitteeCriteriaItemSchema,
  editorialTeamCriteriaItemSchema,
  freeCriteriaItemSchema,
  numberCriteriaItemSchema,
  patentCriteriaItemSchema,
  proceedingCriteriaItemSchema,
  projectCriteriaItemSchema,
  translationCriteriaItemSchema,
} from '@/module/Evaluation/model/EvaluationSchema';
import {
  EvaluationCriteriaDataItemRequest,
  EvaluationCriteriaDiscriminator,
  EvaluationCriteriaType,
} from '@/module/Evaluation/model/EvaluationTypes';

import { NumberInputControl, TextInputControl } from '@/core/components';

import { Id, assertUnreachable } from '@feature/utils';

interface BaseEvaluationCriteriaItemFormProps<T extends BaseCriteriaItemSchema> {
  onSubmit: (request: EvaluationCriteriaDataItemRequest) => void;
  onCancel: () => void;

  defaultValues: DefaultValues<T>;

  isSubmitting: boolean;
  discriminatorList: EvaluationCriteriaDiscriminator[];

  criteriaType: EvaluationCriteriaType;

  dataEntryHint: string | null;

  evaluationId: Id;
  authorId: Id;
  orderNo: number;

  alreadySelectedIds: number[];
}

function renderExtraFormFields(
  isSubmitting: boolean,
  criteriaType: EvaluationCriteriaType,
  t: TFunction,
  dataEntryHint: string | null,
  authorId: Id,
  evaluationId: Id,
  orderNo: number,
  alreadySelectedIds: number[],
) {
  if (criteriaType === 'FREE_ANSWER') {
    return (
      <TextInputControl
        withAsterisk
        name="activityDescription"
        label={t('evaluation.form.activityDescription')}
        isSubmitting={isSubmitting}
      />
    );
  }

  if (criteriaType === 'BOOLEAN') {
    return (
      <Text
        fw="500"
        c="dimmed"
      >
        {dataEntryHint}
      </Text>
    );
  }

  if (criteriaType === 'NUMBER') {
    return (
      <NumberInputControl
        withAsterisk
        name="number"
        label={dataEntryHint ?? t('evaluation.form.number')}
        isSubmitting={isSubmitting}
        allowDecimal={true}
        min={0}
      />
    );
  }

  if (criteriaType === 'SELECT_COURSE') {
    return (
      <BaseEvaluationSelectControl<Course>
        label="Curs"
        renderItem={course => <CourseCard course={course} />}
        getTitle={course => course.title}
        getId={course => course.id}
        onView={course => {
          window.open(`/author/course/${course.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="course"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_BOOK') {
    return (
      <BaseEvaluationSelectControl<Book>
        label="Carte"
        renderItem={book => <BookCard book={book} />}
        getTitle={book => book.title}
        getId={book => book.id}
        onView={book => {
          window.open(`/author/book/${book.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="book"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_ARTICLE') {
    return (
      <BaseEvaluationSelectControl<Article>
        label="Articol"
        renderItem={article => <ArticleCard article={article} />}
        getTitle={article => article.title}
        getId={article => article.id}
        onView={article => {
          window.open(`/author/article/${article.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="article"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_PROCEEDING') {
    return (
      <BaseEvaluationSelectControl<Proceeding>
        label="Proceeding"
        renderItem={proceeding => <ProceedingCard proceeding={proceeding} />}
        getTitle={proceeding => proceeding.title}
        getId={proceeding => proceeding.id}
        onView={proceeding => {
          window.open(`/author/proceeding/${proceeding.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="proceeding"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_TRANSLATION') {
    return (
      <BaseEvaluationSelectControl<Translation>
        label="Traducere"
        renderItem={translation => <TranslationCard translation={translation} />}
        getTitle={translation => translation.title}
        getId={translation => translation.id}
        onView={translation => {
          window.open(`/author/translation/${translation.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="translation"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_CHAPTER') {
    return (
      <BaseEvaluationSelectControl<Chapter>
        label="Capitol"
        renderItem={chapter => <ChapterCard chapter={chapter} />}
        getTitle={chapter => chapter.title}
        getId={chapter => chapter.id}
        onView={chapter => {
          window.open(`/author/chapter/${chapter.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="chapter"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_COMMITTEE') {
    return (
      <BaseEvaluationSelectControl<Committee>
        label="Comisie"
        renderItem={committee => <CommitteeCard committee={committee} />}
        getTitle={committee => committee.title}
        getId={committee => committee.id}
        onView={committee => {
          window.open(`/author/committee/${committee.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="committee"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_PROJECT') {
    return (
      <BaseEvaluationSelectControl<Project>
        label="Proiect"
        renderItem={project => <ProjectCard project={project} />}
        getTitle={project => project.title}
        getId={project => project.id}
        onView={project => {
          window.open(`/author/project/${project.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="project"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_PATENT') {
    return (
      <BaseEvaluationSelectControl<Patent>
        label="Brevet"
        renderItem={patent => <PatentCard patent={patent} />}
        getTitle={patent => patent.title}
        getId={patent => patent.id}
        onView={patent => {
          window.open(`/author/patent/${patent.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="patent"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_EDITORIAL_TEAM') {
    return (
      <BaseEvaluationSelectControl<EditorialTeam>
        label="Colective de redacție / editorial"
        renderItem={et => <EditorialTeamCard editorialTeam={et} />}
        getTitle={et => et.title}
        getId={et => et.id}
        onView={et => {
          window.open(`/author/editorial-team/${et.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="editorialTeam"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_DOCTORAL_COMMITTEE') {
    return (
      <BaseEvaluationSelectControl<DoctoralCommittee>
        label="Îndrumare / Susținere doctorat"
        renderItem={dc => <DoctoralCommitteeCard doctoralCommittee={dc} />}
        getTitle={dc => dc.title}
        getId={dc => dc.id}
        onView={dc => {
          window.open(`/author/doctoral-committee/${dc.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="committee"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  if (criteriaType === 'SELECT_ABSTRACT') {
    return (
      <BaseEvaluationSelectControl<Abstract>
        label="Rezumat"
        renderItem={abstract => <AbstractCard abstract={abstract} />}
        getTitle={abstract => abstract.title}
        getId={abstract => abstract.id}
        onView={abstract => {
          window.open(`/author/abstract/${abstract.id}`);
        }}
        authorId={authorId}
        evaluationId={evaluationId}
        orderNo={orderNo}
        name="abstractItem"
        isSubmitting={isSubmitting}
        alreadySelectedIds={alreadySelectedIds}
        onFilter={(op, search) => {
          const titleMatch = op.title.toLowerCase().indexOf(search.toLowerCase()) !== -1;
          const authorsMatch =
            op.authors.filter(au => au.userName.toLowerCase().indexOf(search.toLowerCase()) !== -1).length > 0;

          return titleMatch || authorsMatch;
        }}
      />
    );
  }

  assertUnreachable(criteriaType);
}

function getSchema(criteriaType: EvaluationCriteriaType) {
  if (criteriaType === 'FREE_ANSWER') {
    return freeCriteriaItemSchema;
  }

  if (criteriaType === 'BOOLEAN') {
    return booleanCriteriaItemSchema;
  }

  if (criteriaType === 'NUMBER') {
    return numberCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_COURSE') {
    return courseCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_ABSTRACT') {
    return abstractCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_ARTICLE') {
    return articleCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_COMMITTEE') {
    return committeeCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_PROJECT') {
    return projectCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_PATENT') {
    return patentCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_CHAPTER') {
    return chapterCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_BOOK') {
    return bookCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_TRANSLATION') {
    return translationCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_PROCEEDING') {
    return proceedingCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_EDITORIAL_TEAM') {
    return editorialTeamCriteriaItemSchema;
  }

  if (criteriaType === 'SELECT_DOCTORAL_COMMITTEE') {
    return doctoralCommitteeCriteriaItemSchema;
  }

  assertUnreachable(criteriaType);
}

export function EvaluationCriteriaItemForm<T extends BaseCriteriaItemSchema>(
  props: BaseEvaluationCriteriaItemFormProps<T>,
) {
  const {
    onSubmit,
    isSubmitting,
    onCancel,
    defaultValues,
    discriminatorList,
    criteriaType,
    dataEntryHint,
    authorId,
    evaluationId,
    orderNo,
    alreadySelectedIds,
  } = props;
  const { t } = useTranslation();
  return (
    <BaseEvaluationCriteriaItemForm
      onCancel={onCancel}
      // @ts-expect-error todo check
      schema={getSchema(criteriaType)}
      onChange={onSubmit}
      isSubmitting={isSubmitting}
      defaultValues={defaultValues}
      discriminatorList={discriminatorList}
      renderExtraFields={() =>
        renderExtraFormFields(
          isSubmitting,
          criteriaType,
          t,
          dataEntryHint,
          authorId,
          evaluationId,
          orderNo,
          alreadySelectedIds,
        )
      }
    />
  );
}
